// Typed client for the Provider Profile Engine (v2)
// Wired for Bootpreneurs (pp_engine_* tables, pp_marketplace_profiles view).
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";

const FN = "provider-profile-engine";
const SOURCES_BUCKET = "provider-sources";
const PHOTOS_BUCKET = "provider-photos";
const PROFILES_TABLE = "pp_engine_profiles" as const;
const SOURCES_TABLE = "pp_engine_sources" as const;
const AUDIT_TABLE = "pp_engine_audit_log" as const;
const TAXONOMY_TABLE = "pp_engine_taxonomy" as const;
const MARKETPLACE_VIEW = "pp_marketplace_profiles" as const;

export type Lang = "en" | "fr";
export interface Bilingual { en: string; fr: string }
export type SkillLevel = "beginner" | "intermediate" | "advanced" | "expert";
export interface Skill { name: string; level?: SkillLevel; years?: number | null; taxonomy_code?: string | null }
export interface Experience { organization: string; role: string; period: string; summary: Bilingual }
export interface Recommendation { author: string; role?: string; text: Bilingual }
export type ServiceFormat = "hourly" | "package" | "retainer" | "program" | "one_off";
export interface Service { name: Bilingual; description: Bilingual; format: ServiceFormat; ideal_client?: Bilingual; taxonomy_code?: string | null }
export interface ProfileCore {
  full_name: string; title: Bilingual; country: string; location: string;
  headline: Bilingual; description: Bilingual; photo_present?: boolean;
}
export interface EvidenceItem { quote: string; source: string }
export interface Curated {
  source_language?: string;
  profile: ProfileCore;
  skills: Skill[];
  experiences: Experience[];
  recommendations?: Recommendation[];
  services: Service[];
  confidence?: Record<string, number>;
  evidence?: Record<string, EvidenceItem>;
  needs_review?: string[];
  extraction_notes?: string;
}
export interface BenchmarkComparable {
  label: string; price_low?: number | null; price_high?: number | null;
  unit?: string; source_title?: string; source_url: string; source_quality?: string;
}
export type MarketPosition = "below_market" | "at_market" | "above_market" | "premium" | "insufficient_data";
export interface BenchmarkItem {
  service: string; unit: string; recommended_range: { low: number; high: number };
  market_position: MarketPosition; evidence_quality?: "high" | "medium" | "low";
  comparables: BenchmarkComparable[]; confidence: number; rationale: Bilingual;
}
export interface Benchmark { currency?: string; fx_note?: string; items?: BenchmarkItem[]; methodology?: Bilingual; disclaimer?: Bilingual; error?: string }
export type Verdict = "supported" | "inferred" | "unsupported" | "contradicted";
export interface VerificationField { path: string; verdict: Verdict; corrected_value?: string | null; confidence: number }
export type Severity = "low" | "medium" | "high";
export interface RiskFlag { path: string; claim: string; severity: Severity; reason: string }
export interface Verification { fields?: VerificationField[]; risk_flags?: RiskFlag[]; notes?: string }
export type ProfileStatus =
  | "draft" | "processing" | "provider_review" | "admin_review"
  | "changes_requested" | "published" | "rejected" | "archived" | "failed";
export interface ProfileRow {
  id: string; provider_id: string; status: ProfileStatus; engine_version: string;
  source_language?: string; linkedin_url?: string;
  full_name?: string; title?: string; country?: string; location?: string; photo_path?: string;
  extracted?: Curated; curated?: Curated; benchmark?: Benchmark; published_snapshot?: Curated;
  evidence?: Record<string, EvidenceItem>; verification?: Verification; risk_flags?: RiskFlag[];
  taxonomy_codes?: string[]; benchmark_as_of?: string; needs_review?: string[]; overall_confidence?: number;
  consent_accuracy?: boolean; consent_display_rights?: boolean; consent_processing?: boolean; consent_at?: string;
  admin_notes?: string; rejection_reason?: string; provider_approved_at?: string;
  admin_reviewed_at?: string; published_at?: string; error?: string;
  created_at: string; updated_at: string;
}
export interface AuditRow { id: string; profile_id: string; actor_role: string; action: string; from_status?: string; to_status?: string; notes?: string; created_at: string; }
export interface SourceRow { id: string; profile_id: string; kind: string; storage_path?: string; text_excerpt?: string; byte_size?: number; created_at: string }
export interface MatchResult { id: string; full_name: string; title: string; country: string; location?: string; photo_path?: string; taxonomy_codes?: string[]; similarity: number }
export interface TaxonomyRow { code: string; kind: "skill" | "service" | "domain"; label_en: string; label_fr: string }
export interface Consent { accuracy: boolean; display_rights: boolean; processing: boolean }
export type BookingStatus = "requested" | "accepted" | "declined" | "cancelled" | "completed";
export interface Booking {
  id: string; profile_id: string; provider_id: string; buyer_id: string;
  service_index?: number | null; service_label?: string | null; message?: string | null;
  proposed_start?: string | null; budget_amount?: number | null; budget_currency?: string | null;
  status: BookingStatus; provider_response?: string | null; responded_at?: string | null;
  created_at: string; updated_at: string;
}

export function bi(v: Bilingual | string | undefined, lang: Lang): string {
  if (v == null) return "";
  return typeof v === "string" ? v : ((v as Bilingual)[lang] || (v as Bilingual).en || (v as Bilingual).fr || "");
}
export function getPath(obj: any, path: string): any {
  return path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
}
const SEVERITY_WEIGHT: Record<Severity, number> = { low: 1, medium: 3, high: 8 };
export function riskScore(row: ProfileRow): number {
  const flags = row.risk_flags ?? [];
  const flagScore = flags.reduce((s, f) => s + (SEVERITY_WEIGHT[f.severity] ?? 1), 0);
  const lowConf = 1 - (row.overall_confidence ?? 0.5);
  return flagScore * 10 + Math.round(lowConf * 10);
}
const DIFF_PATHS = [
  "profile.full_name", "profile.title.en", "profile.title.fr", "profile.country",
  "profile.location", "profile.headline.en", "profile.headline.fr",
  "profile.description.en", "profile.description.fr",
];
export interface FieldDiff { path: string; before: string; after: string }
export function diffProfiles(extracted?: Curated, curated?: Curated): FieldDiff[] {
  if (!extracted || !curated) return [];
  const out: FieldDiff[] = [];
  for (const p of DIFF_PATHS) {
    const a = String(getPath(extracted, p) ?? "");
    const b = String(getPath(curated, p) ?? "");
    if (a !== b) out.push({ path: p, before: a, after: b });
  }
  const count = (c: Curated, k: "skills" | "services" | "experiences") => (c[k] ?? []).length;
  for (const k of ["skills", "services", "experiences"] as const) {
    const a = count(extracted, k), b = count(curated, k);
    if (a !== b) out.push({ path: `${k} (count)`, before: String(a), after: String(b) });
  }
  return out;
}

export function createProfileEngine(client: SupabaseClient = supabase as unknown as SupabaseClient) {
  const sb = client as any;
  async function invoke<T = any>(body: any): Promise<T> {
    const { data, error } = await sb.functions.invoke(FN, { body });
    if (error) {
      let msg = error.message || "request failed";
      try {
        const ctx: any = (error as any).context;
        if (ctx && typeof ctx.json === "function") {
          const j = await ctx.json();
          if (j?.error) msg = j.error;
        }
      } catch { /* keep msg */ }
      throw new Error(msg);
    }
    if (data && (data as any).error) throw new Error((data as any).error);
    return data as T;
  }

  return {
    analyze: (p: { sources: any[]; profile_id?: string; linkedin_url?: string; hints?: Record<string, unknown> }) =>
      invoke<{ profile_id: string; status: ProfileStatus }>({ action: "analyze", ...p }),
    save: (p: { profile_id: string; curated: Curated; photo_path?: string; linkedin_url?: string }) =>
      invoke<{ ok: true }>({ action: "provider_save", ...p }),
    approve: (p: { profile_id: string; curated: Curated; consent: Consent; photo_path?: string }) =>
      invoke<{ ok: true; status: ProfileStatus }>({ action: "provider_approve", ...p }),
    adminDecision: (p: { profile_id: string; decision: "approve" | "request_changes" | "reject"; notes?: string }) =>
      invoke<{ ok: true; status: ProfileStatus }>({ action: "admin_decision", ...p }),
    adminUnpublish: (profile_id: string) => invoke<{ ok: true }>({ action: "admin_unpublish", profile_id }),
    rebenchmark: (p: { profile_id: string; hints?: Record<string, unknown> }) =>
      invoke<{ ok: true; status: string }>({ action: "rebenchmark", ...p }),
    match: (p: { query: string; match_count?: number; min_similarity?: number; filter_codes?: string[] }) =>
      invoke<{ matches: MatchResult[] }>({ action: "match", ...p }),
    remove: (profile_id: string) => invoke<{ ok: true; deleted: true }>({ action: "provider_delete", profile_id }),
    get: (profile_id: string) => invoke<{ profile: ProfileRow }>({ action: "get", profile_id }).then((r) => r.profile),

    // Bookings
    bookingCreate: (p: { profile_id: string; service_index?: number; service_label?: string; message?: string; proposed_start?: string; budget_amount?: number; budget_currency?: string }) =>
      invoke<{ ok: true; booking: Booking }>({ action: "booking_create", ...p }),
    bookingRespond: (p: { booking_id: string; decision: "accept" | "decline"; note?: string }) =>
      invoke<{ ok: true; booking: Booking }>({ action: "booking_respond", ...p }),
    bookingCancel: (booking_id: string) => invoke<{ ok: true; booking: Booking }>({ action: "booking_cancel", booking_id }),
    bookingComplete: (booking_id: string) => invoke<{ ok: true; booking: Booking }>({ action: "booking_complete", booking_id }),
    bookingList: (role?: "buyer" | "provider" | "all") => invoke<{ bookings: Booking[] }>({ action: "booking_list", role }),

    listMine: async (providerId: string) =>
      (await sb.from(PROFILES_TABLE).select("*").eq("provider_id", providerId)
        .order("updated_at", { ascending: false })).data as ProfileRow[] | null,
    adminQueue: async () =>
      (await sb.from(PROFILES_TABLE).select("*").eq("status", "admin_review")
        .order("provider_approved_at", { ascending: true })).data as ProfileRow[] | null,
    marketplace: async () =>
      (await sb.from(MARKETPLACE_VIEW).select("*")
        .order("published_at", { ascending: false })).data as any[] | null,
    marketplaceOne: async (id: string) =>
      (await sb.from(MARKETPLACE_VIEW).select("*").eq("id", id).single()).data as any | null,
    getAudit: async (profileId: string) =>
      (await sb.from(AUDIT_TABLE).select("*").eq("profile_id", profileId)
        .order("created_at", { ascending: false })).data as AuditRow[] | null,
    getSources: async (profileId: string) =>
      (await sb.from(SOURCES_TABLE).select("*").eq("profile_id", profileId)).data as SourceRow[] | null,
    getTaxonomy: async () =>
      (await sb.from(TAXONOMY_TABLE).select("*").order("kind")).data as TaxonomyRow[] | null,

    async uploadSource(userId: string, file: File): Promise<string> {
      const path = `${userId}/${Date.now()}-${safeName(file.name)}`;
      const { error } = await sb.storage.from(SOURCES_BUCKET).upload(path, file, { upsert: false });
      if (error) throw new Error(error.message);
      return path;
    },
    async uploadPhoto(userId: string, file: File): Promise<string> {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${userId}/headshot-${Date.now()}.${ext}`;
      const { error } = await sb.storage.from(PHOTOS_BUCKET).upload(path, file, { upsert: true });
      if (error) throw new Error(error.message);
      return path;
    },
    photoUrl(path?: string): string | null {
      if (!path) return null;
      // Allow absolute URLs (e.g. hosted in public-assets bucket) to pass through unchanged.
      if (/^https?:\/\//i.test(path)) return path;
      return sb.storage.from(PHOTOS_BUCKET).getPublicUrl(path).data.publicUrl;
    },
    async sourceSignedUrl(path: string, seconds = 300): Promise<string | null> {
      const { data } = await sb.storage.from(SOURCES_BUCKET).createSignedUrl(path, seconds);
      return data?.signedUrl ?? null;
    },

    onProfile(profileId: string, cb: (row: ProfileRow) => void): () => void {
      const ch = sb
        .channel(`pe-${profileId}`)
        .on("postgres_changes",
          { event: "UPDATE", schema: "public", table: PROFILES_TABLE, filter: `id=eq.${profileId}` },
          (payload: any) => cb(payload.new as ProfileRow))
        .subscribe();
      return () => { sb.removeChannel(ch); };
    },
  };
}
export type ProfileEngine = ReturnType<typeof createProfileEngine>;

function safeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-60);
}